import React, { useMemo } from 'react';
import { buttonSizes, buttonVariants } from 'src/tef/components/config/variantConfig';
import { useIcon } from 'src/tef/components/hooks/useIcon';
import { useResponsiveVariant } from 'src/tef/components/hooks/useResponsiveVariant';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import { getBorderRadiusBySize } from 'src/tef/components/utils/getBorderRadius';
import type { ForwardedHTMLProps } from 'src/tef/components/utils/types';
import { ComponentVariant, IconProp } from 'src/tef/components/utils/types';

interface ActionButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'>, ForwardedHTMLProps {
  children?: React.ReactNode;
  variant?: ComponentVariant;
  icon?: IconProp;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  minWidth?: string | number;
  className?: string;
}

export const ActionButton = React.forwardRef<HTMLButtonElement, ActionButtonProps>(
  (
    {
      children,
      variant = { style: 'primary', size: 'md' },
      icon,
      iconPosition = 'left',
      loading = false,
      disabled,
      className,
      minWidth,
      ...props
    },
    ref
  ) => {
    const tef = useTEF();
    const forwardedProps = forwardProps(props);
    const iconElement = useIcon(icon);

    const variantStyle = variant.style || 'primary';
    const variantSize = variant.size || 'md';
    const responsiveSize = useResponsiveVariant(variantSize);
    const resolvedSize = typeof responsiveSize === 'string' ? responsiveSize : 'md';
    const sizeConfig = typeof resolvedSize === 'string' ? buttonSizes[resolvedSize] : buttonSizes.md;

    const buttonVariant = buttonVariants[variantStyle as keyof typeof buttonVariants] || buttonVariants.primary;
    const isDisabled = disabled || loading;
    const borderRadius = getBorderRadiusBySize(resolvedSize, tef);

    const buttonClasses = useMemo(() => {
      return cn(
        'relative inline-flex items-center justify-center',
        sizeConfig.gap,
        'font-semibold',
        'transition-colors duration-150',
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary',
        'focus-visible:ring-2',
        'select-none',
        'whitespace-nowrap',
        sizeConfig.padding,
        sizeConfig.text,
        borderRadius,
        buttonVariant.base,
        !isDisabled && buttonVariant.hover,
        !isDisabled && buttonVariant.active,
        isDisabled && buttonVariant.disabled,
        isDisabled && 'cursor-not-allowed',
        className
      );
    }, [sizeConfig, isDisabled, buttonVariant, borderRadius, className]);

    const iconSize = sizeConfig.icon;
    const hasLeftIcon = icon && iconPosition === 'left' && !loading;
    const hasRightIcon = icon && iconPosition === 'right' && !loading;

    const spinnerSizes = {
      xs: 'w-3 h-3',
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
      xl: 'w-7 h-7',
    };
    const spinnerSize = spinnerSizes[resolvedSize as keyof typeof spinnerSizes] || 'w-5 h-5';

    return (
      <button
        ref={ref}
        type={props.type || 'button'}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={loading}
        className={buttonClasses}
        style={minWidth ? { minWidth } : undefined}
        {...forwardedProps}
        {...props}
      >
        {loading && (
          <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
            <span
              className={cn(
                spinnerSize,
                'border-2 border-current border-t-transparent rounded-full animate-spin inline-block'
              )}
            />
          </span>
        )}
        {hasLeftIcon && (
          <span className={cn('inline-flex items-center justify-center flex-shrink-0', iconSize)} aria-hidden="true">
            {iconElement}
          </span>
        )}
        <span className={loading ? 'opacity-0 invisible' : undefined}>{children}</span>
        {loading && <span className="sr-only">Loading...</span>}
        {hasRightIcon && (
          <span className={cn('inline-flex items-center justify-center flex-shrink-0', iconSize)} aria-hidden="true">
            {iconElement}
          </span>
        )}
      </button>
    );
  }
);

ActionButton.displayName = 'ActionButton';
